import { useState } from "react";
import { Link } from "react-router-dom";
import { API_HOST } from "../constants";
import { CheckCircle, Star, MessageSquare, Send } from "lucide-react";
import type { TelegramUser } from "../types";

interface ModuleCompletionProps {
  courseSlug: string;
  moduleId: string;
  moduleTitle: string;
  user: TelegramUser | null;
  onClose?: () => void;
}

export const ModuleCompletion = ({
  courseSlug,
  moduleId,
  moduleTitle,
  user,
  onClose,
}: ModuleCompletionProps) => {
  const [rating, setRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!rating) {
      setError("Пожалуйста, оцените модуль");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_HOST}/api/module-feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          course_slug: courseSlug,
          module_id: moduleId,
          module_title: moduleTitle,
          rating,
          feedback: feedback.trim() || null,
          user_id: user?.id || null,
          user_name: user?.first_name || user?.username || "Анонимный пользователь",
        }),
      });

      if (!response.ok) {
        throw new Error("Не удалось отправить отзыв");
      }

      setIsSubmitted(true);
      
      // Закрываем модалку через 2 секунды после успешной отправки
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 2000);
    } catch (err) {
      console.error("Error submitting module feedback:", err);
      setError("Не удалось отправить отзыв. Попробуйте позже.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle className="w-16 h-16 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-text mb-2">Спасибо за отзыв!</h2>
          <p className="text-neutral-text/70 mb-4">
            Ваш отзыв поможет нам улучшить курс.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 sm:p-8">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-neutral-text mb-2">
            Модуль завершен!
          </h2>
          <p className="text-neutral-text/70">
            Вы успешно завершили модуль <strong>"{moduleTitle}"</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Оценка */}
          <div>
            <label className="block text-sm font-medium text-neutral-text mb-3">
              Оцените модуль <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center justify-center gap-2">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRating(value)}
                  className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${
                    rating === value
                      ? "bg-primary text-white scale-110"
                      : "bg-neutral-light text-neutral-text/60 hover:bg-neutral-border"
                  }`}
                >
                  <Star
                    className={`w-6 h-6 ${
                      rating && rating >= value ? "fill-current" : ""
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Отзыв */}
          <div>
            <label
              htmlFor="feedback"
              className="block text-sm font-medium text-neutral-text mb-2"
            >
              Ваш отзыв (необязательно)
            </label>
            <textarea
              id="feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Что вам понравилось? Что можно улучшить?"
              rows={4}
              className="w-full px-4 py-3 border border-neutral-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Кнопки */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              disabled={isSubmitting || !rating}
              className="flex-1 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Отправка...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Отправить отзыв
                </>
              )}
            </button>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-6 py-3 border border-neutral-border rounded-lg hover:bg-neutral-light transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Пропустить
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
